import type { IOrganizationEnablement } from "models/Organization.model";
import agent, { type NGCAPIKeySpec, type NGCEnablement } from "server";

export const AI_FOUNDATIONS_ENABLEMENT = "AI_FOUNDATIONS";
export const AI_FOUNDATIONS_ENABLEMENT_REQUEST: NGCEnablement = 2;

type OrganizationWithNGCEnablement = {
  id?: string;
  ngcEnablements?: IOrganizationEnablement[];
};

export const hasNGCEnablement = (
  org: OrganizationWithNGCEnablement | null | undefined,
  enablement: string,
) =>
  org?.ngcEnablements?.some(
    ({ ProductName, productName, product_name }) =>
      ProductName === enablement ||
      productName === enablement ||
      product_name === enablement,
  ) ?? false;

interface EnsureNGCEnablementForAPIKeyOptions {
  ngcApiKeySpec?: NGCAPIKeySpec | null;
  activeOrg?: OrganizationWithNGCEnablement | null;
  activeOrgId?: string | null;
  refreshOrgs: () => Promise<OrganizationWithNGCEnablement[]>;
}

export const ensureNGCEnablementForAPIKey = async ({
  ngcApiKeySpec,
  activeOrg,
  activeOrgId,
  refreshOrgs,
}: EnsureNGCEnablementForAPIKeyOptions) => {
  if (!ngcApiKeySpec) return true;
  if (hasNGCEnablement(activeOrg, AI_FOUNDATIONS_ENABLEMENT)) return true;

  const orgId = activeOrg?.id || activeOrgId;
  if (!orgId) return false;

  try {
    const res = await agent.Organizations.createNGCEnablements(
      orgId,
      [AI_FOUNDATIONS_ENABLEMENT_REQUEST],
    );
    if (!res.success) return false;

    try {
      const refreshedOrgs = await refreshOrgs();
      const refreshedActiveOrg = refreshedOrgs.find((org) => org.id === orgId);
      if (!hasNGCEnablement(refreshedActiveOrg, AI_FOUNDATIONS_ENABLEMENT)) {
        console.warn(
          "NGC enablement was created but refreshed organization data does not include it",
          {
            orgId,
            enablement: AI_FOUNDATIONS_ENABLEMENT,
          },
        );
      }
    } catch (err) {
      console.error("Failed to refresh orgs after NGC enablement", err);
    }

    return true;
  } catch (err) {
    console.error("Failed to add NGC enablement", err);
    return false;
  }
};
